import { collectMediaIds, type PanoVariants } from '@vrlearning/lesson-schema';

/**
 * The headset's media cache (posters spec, "Offline" and "Eviction").
 *
 * The service worker keeps every photo it fetched from `/v1/media/<id>` under
 * its path, the signature stripped (vite.config.ts): a signed URL changes on
 * every lesson load, the id does not. So a photo removed from the server
 * would stay on a headset for as long as the cache keeps it -- unless the
 * headset lets go of it. It does that here: when a lesson arrives from the
 * API, every id the lesson names that the API no longer signs is deleted.
 * A headset that stays offline keeps showing it until its next online load.
 */

/** The Workbox cache's name (vite.config.ts). */
export const MEDIA_CACHE = 'media';

/** Where the service worker keeps a media URL: its path on this origin, no query. */
export function mediaCacheKey(url: string, origin: string): string {
  const parsed = new URL(url, origin);
  return `${parsed.origin}${parsed.pathname}`;
}

/** The variants a 360° photo is cached under beside its JPEG (ADR-041): `/v1/media/<id>/<variant>`. */
const PANO_VARIANTS = ['cube', 'light'] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** The media ids a lesson's 360° photos use: the scene's and each step's. Read leniently. */
function panoIds(lesson: unknown): string[] {
  if (!isRecord(lesson) || !isRecord(lesson['scene'])) return [];
  if (lesson['scene']['type'] !== 'pano360') return [];
  const ids = new Set<string>();
  const add = (photo: unknown): void => {
    if (isRecord(photo) && typeof photo['media'] === 'string') ids.add(photo['media']);
  };
  add(lesson['scene']);
  const steps = lesson['steps'];
  if (Array.isArray(steps)) for (const step of steps) if (isRecord(step)) add(step['pano']);
  return [...ids];
}

/**
 * Deletes the cached photos of ids `lesson` names and `media` has no URL for,
 * and a 360° photo's cube and light copy (ADR-041) when the photo is unsigned
 * or its variants are not signed (its cube is being made again: CacheFirst
 * would otherwise keep showing the old one). Returns the ids whose file it let
 * go of. Never throws.
 */
export async function evictUnsigned(
  lesson: unknown,
  media: Readonly<Record<string, string>>,
  variants: Readonly<PanoVariants>,
  storage: Pick<CacheStorage, 'open'> | undefined = globalThis.caches,
  origin: string = globalThis.location?.origin ?? '',
): Promise<string[]> {
  const unsigned = collectMediaIds(lesson).filter((id) => media[id] === undefined);
  const unvaried = panoIds(lesson).filter((id) => !Object.hasOwn(variants, id));
  if ((unsigned.length === 0 && unvaried.length === 0) || !storage) return [];
  const key = (id: string, variant?: string): string =>
    mediaCacheKey(
      `/v1/media/${encodeURIComponent(id)}${variant === undefined ? '' : `/${variant}`}`,
      origin,
    );
  try {
    const cache = await storage.open(MEDIA_CACHE);
    const evicted: string[] = [];
    for (const id of unsigned) {
      if (await cache.delete(key(id))) evicted.push(id);
    }
    for (const id of unvaried) {
      for (const variant of PANO_VARIANTS) await cache.delete(key(id, variant));
    }
    return evicted;
  } catch {
    return [];
  }
}
